/**
 * verifyPasskeyEnrollment — stores a newly minted passkey against the caller's
 * own account.
 *
 * A passkey proves a device, never a mailbox, so this demands an
 * already-authenticated session the product's `assertAccount` admits, and the
 * credential binds server-side to the uid that session carries and to no
 * other. The challenge is spent in a transaction requiring the stored uid to
 * equal the caller's own.
 *
 * Fields justified:
 *   credentialId      — the credential's own id as the authenticator emits it;
 *                       its sha256 addresses the document, and the door refuses
 *                       a request whose id differs from the one inside the
 *                       signed authenticator data.
 *   attestationObject — the CBOR structure carrying the authenticator data and,
 *                       inside it, the COSE public key every later assertion is
 *                       verified against. `attestation: none` is requested, so
 *                       no certificate chain is ever read from it.
 *   clientDataJSON    — the browser's own record: the origin (the anti-phishing
 *                       binding the user agent writes, not the caller), the
 *                       type pinned to the creation string, and the challenge
 *                       whose hash is the stored document's id.
 * Output:
 *   (none — the caller already holds the session; an `ok: true` would be a
 *    second name for "the call did not throw".)
 * Excluded:
 *   uid        — read from the caller's token, never supplied.
 *   publicKey  — inside `attestationObject`, decoded server-side.
 *   transports / authenticatorAttachment — nothing reads them.
 */
import { z } from 'zod';
import { base64url } from './base64url.ts';

export const VerifyPasskeyEnrollmentInput = z
  .object({
    credentialId: base64url.describe(
      "the authenticator's own id for the credential it just minted, as base64url",
    ),
    attestationObject: base64url.describe(
      'the CBOR attestation the authenticator returned, carrying the authenticator data and the new public key, as base64url',
    ),
    clientDataJSON: base64url.describe(
      "the browser's own record of the ceremony — origin, type and challenge — as base64url",
    ),
  })
  .strict();
export type VerifyPasskeyEnrollmentInput = z.infer<typeof VerifyPasskeyEnrollmentInput>;

export const VerifyPasskeyEnrollmentOutput = z.object({}).strict();
export type VerifyPasskeyEnrollmentOutput = z.infer<typeof VerifyPasskeyEnrollmentOutput>;
